import React from 'react';
import { useTranslation } from 'react-i18next';
import { fmt, occupancyPct } from '../data/metrics';
import type { ConnectedSourceId, Outlet } from '../types';
import {
  Icon,
  Ring,
  Spark,
  STATUS_META,
  statusOf,
  StatusDot,
} from './shared/Icon';
import { OutletActions } from './OutletActions';

export function FocusDrawer({
  outlet,
  connected,
  onClose,
}: {
  outlet: Outlet;
  connected: ConnectedSourceId[];
  onClose: () => void;
}) {
  const { t } = useTranslation('live');
  const occ = occupancyPct(outlet);
  const st = statusOf(outlet);
  const posOn = connected.includes('pos');
  const reviewsOn = connected.includes('reviews');
  const weatherOn = connected.includes('weather') && !!outlet.weather;
  const eventsOn =
    connected.includes('events') && !!outlet.events && outlet.events.length > 0;
  const paxPerTable =
    outlet.occTables > 0
      ? (outlet.seated / outlet.occTables).toFixed(1)
      : '-';
  const statusLabel = STATUS_META[st]?.label ?? STATUS_META.quiet.label;

  return (
    <>
      <div
        className="scrim"
        onMouseDown={e => {
          // Prevent the subsequent click from reaching the outlet row under the overlay.
          e.preventDefault();
          onClose();
        }}
        role="presentation"
      />
      <div className="drawer" data-ftue="focus-drawer">
        <div className="dr-head">
          <div>
            <div className="focus-head-row">
              <StatusDot status={st} size={11} />
              <h2>{outlet.name}</h2>
            </div>
            <p>
              {outlet.city}
              {outlet.cuisine ? ` · ${outlet.cuisine}` : ''} · {statusLabel}
            </p>
          </div>
          <button
            type="button"
            className="ghost-btn"
            onClick={onClose}
            aria-label={t('focus.close')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="dr-body">
          <div className="focus-hero">
            <Ring pct={occ} size={86} stroke={8} color="var(--accent)">
              {occ}%
            </Ring>
            <div className="focus-hero-side">
              <div className="focus-hero-meta">
                {t('focus.seatsInService', {
                  seated: outlet.seated,
                  seats: outlet.seats,
                  occTables: outlet.occTables,
                  tables: outlet.tables,
                })}
              </div>
              <Spark
                data={outlet.spark}
                w={220}
                h={40}
                color="var(--accent)"
                fillArea
              />
            </div>
          </div>
          <div className="focus-stat">
            <div className="fs-cell">
              <div className="v">{outlet.coversToday}</div>
              <div className="l">{t('focus.guestsToday')}</div>
            </div>
            <div className="fs-cell">
              <div className="v">+{outlet.upcoming}</div>
              <div className="l">{t('focus.incoming')}</div>
            </div>
            <div className="fs-cell">
              <div className="v">{outlet.avgSeatMin}m</div>
              <div className="l">{t('focus.avgSeating')}</div>
            </div>
            <div className="fs-cell">
              <div className="v">{paxPerTable}</div>
              <div className="l">{t('focus.paxTable')}</div>
            </div>
            <div className="fs-cell">
              <div
                className="v"
                style={{
                  color:
                    outlet.noShow >= 9 ? 'var(--st-alert)' : 'inherit',
                }}
              >
                {outlet.noShow}%
              </div>
              <div className="l">{t('focus.noshow')}</div>
            </div>
            <div className="fs-cell">
              <div
                className="v"
                style={{
                  color:
                    outlet.cancelRate >= 10 ? 'var(--st-alert)' : 'inherit',
                }}
              >
                {outlet.cancelRate}%
              </div>
              <div className="l">{t('focus.cancels')}</div>
            </div>
            {posOn ? (
              <div className="fs-cell">
                <div className="v">
                  {fmt.money(outlet.coversToday * (outlet.spendCover ?? 0))}
                </div>
                <div className="l">
                  {t('focus.revenuePerGuest', { amount: outlet.spendCover ?? 0 })}
                </div>
              </div>
            ) : reviewsOn ? (
              <div className="fs-cell">
                <div className="v">{outlet.review}★</div>
                <div className="l">{t('focus.guestRating')}</div>
              </div>
            ) : (
              <div className="fs-cell">
                <div className="v">{outlet.tables}</div>
                <div className="l">{t('focus.tables')}</div>
              </div>
            )}
          </div>
          {(weatherOn || eventsOn) && (
            <div className="fd-signals">
              {weatherOn && (
                <div className="fd-sig">
                  <Icon
                    name={outlet.weather!.icon}
                    size={16}
                    style={{
                      color:
                        outlet.weather!.icon === 'sun'
                          ? 'var(--st-busy)'
                          : 'var(--petrol-ink)',
                    }}
                  />
                  <span>
                    <b>
                      {outlet.weather!.cond} · {outlet.weather!.temp}°
                    </b>{' '}
                    {outlet.weather!.later?.changing ? (
                      <>
                        → <b>{outlet.weather!.later.cond}</b>{' '}
                        {t('focus.byHour', { hour: outlet.weather!.later.hour })} -{' '}
                        {outlet.weather!.later.impact}
                      </>
                    ) : (
                      <>- {(outlet.weather!.later ?? outlet.weather!).impact}</>
                    )}
                  </span>
                </div>
              )}
              {eventsOn &&
                outlet.events!.map((ev, i) => (
                  <div key={`${ev.name}-${i}`} className="fd-sig">
                    <Icon
                      name="ticket"
                      size={15}
                      style={{ color: 'var(--accent)' }}
                    />
                    <span>
                      <b>{ev.name}</b> ·{' '}
                      {ev.dist >= 1000
                        ? `${(ev.dist / 1000).toFixed(1)}km`
                        : `${ev.dist}m`}{' '}
                      · {ev.start}:00{' '}
                      {ev.surge ? (
                        <span className="surge-badge">{t('focus.surge')}</span>
                      ) : (
                        <>· {t('focus.plusGuests', { count: ev.impact })}</>
                      )}
                    </span>
                  </div>
                ))}
            </div>
          )}
          <OutletActions outlet={outlet} connected={connected} />
        </div>
      </div>
    </>
  );
}
